import React, { memo, useRef, useEffect, useState } from 'react';
import { useSpring, animated, config } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';

const PremiumTicketGrid = memo(({ 
  tickets, 
  onTicketClick, 
  selectedTickets = [], 
  isMultiSelectMode = false,
  viewMode = 'grid'
}) => {
  const [hoveredTicket, setHoveredTicket] = useState(null);
  const [loadedTickets, setLoadedTickets] = useState(new Set());
  
  const getTicketStatus = (ticket) => {
    if (ticket.sold) return { status: 'sold', label: 'Vendido', color: 'from-red-500 to-red-600' };
    if (ticket.reserved) return { status: 'reserved', label: 'Reservado', color: 'from-yellow-400 to-orange-500' };
    if (isMultiSelectMode && selectedTickets.find(t => t.id === ticket.id)) {
      return { status: 'selected', label: 'Seleccionado', color: 'from-purple-500 to-pink-500' };
    }
    return { status: 'available', label: 'Disponible', color: 'from-green-400 to-emerald-500' };
  };

  const TicketItem = ({ ticket, index }) => {
    const [ref, inView] = useInView({
      threshold: 0.1,
      triggerOnce: true
    });

    const ticketStatus = getTicketStatus(ticket);
    const isSelected = selectedTickets.find(t => t.id === ticket.id);
    const isHovered = hoveredTicket === ticket.id;

    const springProps = useSpring({
      from: { opacity: 0, transform: 'scale(0.8) rotateY(90deg)' },
      to: { 
        opacity: inView ? 1 : 0, 
        transform: inView ? 'scale(1) rotateY(0deg)' : 'scale(0.8) rotateY(90deg)'
      },
      delay: inView ? index * 20 : 0,
      config: config.wobbly
    });

    const hoverSpring = useSpring({
      transform: isHovered ? 'scale(1.15) translateY(-10px)' : 'scale(1) translateY(0px)',
      boxShadow: isHovered 
        ? '0 20px 40px rgba(0,0,0,0.2), 0 0 40px rgba(99,102,241,0.3)' 
        : '0 4px 20px rgba(0,0,0,0.08)',
      config: config.gentle
    });

    useEffect(() => {
      if (inView && !loadedTickets.has(ticket.id)) {
        setLoadedTickets(prev => new Set([...prev, ticket.id]));
      }
    }, [inView, ticket.id]);

    const handleClick = () => {
      if (!ticket.sold && !ticket.reserved) {
        onTicketClick(ticket);
      }
    };

    if (viewMode === 'compact') {
      return (
        <animated.div
          ref={ref}
          style={springProps}
          onClick={handleClick}
          onMouseEnter={() => setHoveredTicket(ticket.id)}
          onMouseLeave={() => setHoveredTicket(null)}
          className={`
            relative flex items-center justify-between p-3 rounded-xl cursor-pointer
            transition-all duration-300 border-2
            ${ticketStatus.status === 'sold' ? 'border-red-200 bg-red-50' : ''}
            ${ticketStatus.status === 'reserved' ? 'border-yellow-200 bg-yellow-50' : ''}
            ${ticketStatus.status === 'selected' ? 'border-purple-300 bg-purple-50 ring-2 ring-purple-400' : ''}
            ${ticketStatus.status === 'available' ? 'border-gray-200 bg-white hover:border-purple-300' : ''}
            ${(ticket.sold || ticket.reserved) ? 'cursor-not-allowed opacity-75' : ''}
          `}
        >
          <div className="flex items-center space-x-3">
            <div className={`
              w-10 h-10 rounded-full bg-gradient-to-br ${ticketStatus.color}
              flex items-center justify-center text-white font-bold text-sm
            `}>
              {ticket.number}
            </div>
            <span className="font-medium text-gray-800">{ticket.displayNumbers}</span>
          </div>
          <span className={`
            text-sm font-medium
            ${ticketStatus.status === 'sold' ? 'text-red-600' : ''}
            ${ticketStatus.status === 'reserved' ? 'text-yellow-600' : ''}
            ${ticketStatus.status === 'selected' ? 'text-purple-600' : ''}
            ${ticketStatus.status === 'available' ? 'text-green-600' : ''}
          `}>
            {ticketStatus.label}
          </span>
          {isSelected && (
            <div className="absolute -right-1 -top-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center animate-bounce">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </animated.div>
      );
    }

    return (
      <animated.div
        ref={ref}
        style={{ ...springProps, ...hoverSpring }}
        className="relative perspective-1000"
      >
        <div
          onClick={handleClick}
          onMouseEnter={() => setHoveredTicket(ticket.id)}
          onMouseLeave={() => setHoveredTicket(null)}
          className={`
            relative overflow-hidden rounded-2xl p-6 
            cursor-pointer transition-all duration-300
            ${(ticket.sold || ticket.reserved) ? 'cursor-not-allowed' : 'hover:rotate-y-5'}
          `}
        >
          {/* Background Gradient */}
          <div className={`
            absolute inset-0 bg-gradient-to-br ${ticketStatus.color}
            ${ticketStatus.status === 'available' && isHovered ? 'opacity-100' : 'opacity-90'}
            transition-opacity duration-300
          `} />
          
          {/* Shimmer Effect */}
          {ticketStatus.status === 'available' && (
            <div className="absolute inset-0 opacity-30">
              <div className="absolute inset-0 shimmer-effect" />
            </div>
          )}
          
          {/* Holographic Effect for Premium Numbers */}
          {ticket.number % 100 === 0 && ticketStatus.status === 'available' && (
            <div className="absolute inset-0 holographic-effect opacity-20" />
          )}
          
          {/* Content */}
          <div className="relative z-10 text-center text-white">
            {/* Renderizar números con soporte para múltiples oportunidades */}
            {(() => {
              const numbers = ticket.displayNumbers || ticket.number.toString();
              const hasMultipleNumbers = numbers.includes('-');
              
              if (hasMultipleNumbers) {
                // Dividir los números y mostrarlos en líneas separadas
                const numberArray = numbers.split('-');
                return (
                  <div className="flex flex-col items-center">
                    {numberArray.map((num, index) => (
                      <div key={index} className="text-2xl font-bold drop-shadow-lg leading-tight">
                        {num}
                      </div>
                    ))}
                  </div>
                );
              } else {
                // Mostrar número único normalmente
                return (
                  <div className="text-3xl font-bold mb-1 drop-shadow-lg">
                    {numbers}
                  </div>
                );
              }
            })()}
            {ticket.displayNumbers && ticket.displayNumbers !== ticket.number.toString() && (
              <div className="text-xs opacity-80 mt-1">
                #{ticket.number}
              </div>
            )}
            <div className="text-sm mt-2 font-medium">
              {ticketStatus.label}
            </div>
          </div>
          
          {/* Special Indicators */}
          {ticket.reserved && !ticket.sold && (
            <div className="absolute top-2 left-2 bg-yellow-600 text-white rounded-full w-8 h-8 flex items-center justify-center animate-pulse">
              <span className="text-sm font-bold">!</span>
            </div>
          )}
          
          {isSelected && (
            <div className="absolute top-2 right-2 bg-white rounded-full w-8 h-8 flex items-center justify-center animate-bounce shadow-lg">
              <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}
          
          {/* Vendor Badge */}
          {ticket.vendorName && (
            <div className="absolute bottom-2 right-2 bg-black/20 backdrop-blur-sm rounded-full px-2 py-1">
              <span className="text-xs text-white/80">{ticket.vendorName}</span>
            </div>
          )}
          
          {/* Premium Number Badge */}
          {ticket.number % 100 === 0 && (
            <div className="absolute top-2 left-2 animate-pulse">
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                ⭐ PREMIUM
              </div>
            </div>
          )}
        </div>
      </animated.div>
    );
  };

  const gridClasses = viewMode === 'compact' 
    ? 'space-y-2' 
    : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4';

  return (
    <div className="relative">
      {/* Grid Container */}
      <div className={gridClasses}>
        {tickets.map((ticket, index) => (
          <TicketItem key={ticket.id} ticket={ticket} index={index} />
        ))}
      </div>
      
      {/* Empty State */}
      {tickets.length === 0 && (
        <div className="text-center py-20">
          <div className="w-32 h-32 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-16 h-16 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-xl text-gray-600">No hay boletos para mostrar</p>
          <p className="text-gray-400 mt-2">Intenta cambiar los filtros</p>
        </div>
      )}
      
      {/* Loading Overlay */}
      {tickets.length > 100 && loadedTickets.size < tickets.length && (
        <div className="fixed bottom-4 right-4 bg-white rounded-full shadow-lg px-4 py-2">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-purple-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-600">
              Cargando {loadedTickets.size} de {tickets.length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

PremiumTicketGrid.displayName = 'PremiumTicketGrid';

// Add CSS for 3D effects
const style = document.createElement('style');
style.textContent = `
  .perspective-1000 {
    perspective: 1000px;
  }
  
  .hover\\:rotate-y-5:hover {
    transform: rotateY(5deg);
  }
  
  @media (max-width: 640px) {
    .grid-cols-2 {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
`;
document.head.appendChild(style);

export default PremiumTicketGrid;